/**
 * Which layout to render: desktop, or one of the two mobile reductions.
 *
 * The breakpoint itself lives in `constants/density.js`, which is the only
 * place in the app allowed to hold a pixel value — a breakpoint is a density
 * decision, and the doctrine check enforces that (CLAUDE.md §12).
 *
 * A media query rather than `window.innerWidth` read once, because a rotated
 * phone must re-render and a resized window is how anybody tests this.
 */
import { useEffect, useState } from 'react';
import { MOBILE_MEDIA_QUERY } from '../constants/density.js';

export const MOBILE_QUERY = MOBILE_MEDIA_QUERY;

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(MOBILE_QUERY).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = (event) => setIsMobile(event.matches);
    // `addEventListener` with an `addListener` fallback: Safari before 14
    // only has the deprecated form, and a rep's phone is exactly where an old
    // Safari turns up.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    setIsMobile(mql.matches);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  return isMobile;
}
